// src/views/TerminalView.js
// ───────────────────────────────────────────────────────────────────────────
// 📟 Terminal de Operador — ruta /terminal (PLAN-TERMINAL-OPERADOR W2 · B1c)
//
// Cáscara TÁCTIL de pie de máquina: escanear → ver plancha → marcar → siguiente,
// en UNA pantalla, sin sidebar, botones ≥64 px, 100% t('key') (es/en). NO duplica
// lógica: reusa resolveScan/markAvailability (src/lib/sheetLookup.js), la CF
// recordProductionScan (B1a), el agregado de piezas (pieces.mjs) y el flujo de
// falla de /scan (ReportFailureModal + writeBatch). Cámara vía html5-qrcode
// (patrón de QRScannerModal) + input HID (scanner BT C750 "teclea" el QR + Enter).
// ───────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { db, functions } from '../firebase/firebase-config';
import {
  doc, collection, writeBatch, serverTimestamp, arrayUnion,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  Camera, Search, Printer, Scissors, AlertTriangle, CheckCircle, AlertCircle,
  X, LogOut, ArrowLeft, RotateCcw,
} from 'lucide-react';
import { t } from '../lib/i18n';
import { resolveScan, loadSheet, markAvailability } from '../lib/sheetLookup';
import { productionResetFields } from '../lib/production';
import { previewThumb, previewCard } from '../lib/previews';
import { pieceProductionStatus } from '../lib/pieces.mjs';
import { saveLastPath, resolveTimeoutMs } from '../lib/sessionPolicy';
import useIdleLogout from '../hooks/useIdleLogout';
import ReportFailureModal from '../components/JobView/Tabs/Modals/ReportFailureModal';
import { buildTaskAssignment, buildAssignmentEmail, buildAssignedByStamp } from '../utils/taskAssignment';

const PLACEHOLDER_SHEET = 'https://placehold.co/600x360/1f2937/4f46e5?text=N/A';
const PLACEHOLDER_PIECE = 'https://placehold.co/120x120/1f2937/4f46e5?text=N/A';

// Modal de cámara (html5-qrcode) — mismo patrón que QRScannerModal de PiecesInventoryTab.
const CameraScanner = ({ isOpen, onScan, onClose }) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const scanner = new Html5QrcodeScanner('terminal-qr-reader', { qrbox: { width: 250, height: 250 }, fps: 10 }, false);
    const ok = (decodedText) => { scanner.clear().catch(() => {}); onScan(decodedText); };
    scanner.render(ok, () => {});
    return () => { if (scanner && scanner.getState() !== 3) scanner.clear().catch(() => {}); };
  }, [isOpen, onScan]);

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/80 z-[120] flex flex-col items-center justify-center p-4">
      <h2 className="text-white text-2xl font-bold mb-4">{t('terminal.cameraTitle')}</h2>
      <div id="terminal-qr-reader" className="w-full max-w-md bg-white rounded-lg overflow-hidden" />
      <button onClick={onClose} className="mt-6 min-h-[64px] px-8 bg-gray-700 text-white text-xl font-bold rounded-xl flex items-center gap-2">
        <X size={24} /> {t('terminal.cameraClose')}
      </button>
    </div>
  );
};

const Stat = ({ label, value, total }) => (
  <div className="p-4 bg-gray-700 rounded-xl text-center">
    <p className="text-sm text-gray-300">{label}</p>
    <p className="text-3xl font-bold tabular-nums">{value} / {total}</p>
  </div>
);

const TerminalView = ({ user, users = [], onLogout }) => {
  const navigate = useNavigate();
  const [view, setView] = useState('scan');           // 'scan' | 'sheet' | 'piece'
  const [input, setInput] = useState('');
  const [sheet, setSheet] = useState(null);
  const [jobData, setJobData] = useState(null);
  const [piece, setPiece] = useState(null);           // { piece, jobId, jobName }
  const [sheetCandidates, setSheetCandidates] = useState([]);
  const [pieceCandidates, setPieceCandidates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const inputRef = useRef(null);

  // Higiene de sesión: respeta el tipo de estación (tablet-personal = 12 h). Reusa el
  // mismo hook del shell (no se duplica). El aviso es compacto (la tablet casi nunca
  // lo verá por el timeout largo, pero en una estación compartida sigue protegiendo).
  const { warning, secondsLeft, stayActive } = useIdleLogout({ onLogout, timeoutMs: resolveTimeoutMs(user) });

  useEffect(() => { saveLastPath('/terminal'); }, []);
  const focusInput = useCallback(() => { setTimeout(() => inputRef.current?.focus(), 0); }, []);
  useEffect(() => { if (view === 'scan' && !cameraOpen) focusInput(); }, [view, cameraOpen, focusInput]);

  const resetToScan = () => {
    setView('scan');
    setSheet(null); setJobData(null); setPiece(null);
    setSheetCandidates([]); setPieceCandidates([]);
    setError(''); setSuccess(''); setInput('');
    focusInput();
  };

  const showSheet = (loaded) => {
    setSheet(loaded.sheet); setJobData(loaded.jobData);
    setPiece(null); setSheetCandidates([]); setPieceCandidates([]);
    setView('sheet'); setInput('');
  };

  // Resuelve cualquier escaneo/texto (mismo motor que /scan: src/lib/sheetLookup).
  const handleResolve = async (raw) => {
    if (!String(raw || '').trim()) return;
    setBusy(true); setError(''); setSuccess('');
    setSheetCandidates([]); setPieceCandidates([]);
    try {
      const res = await resolveScan(db, raw);
      switch (res.status) {
        case 'sheet': showSheet(res); break;
        case 'sheetCandidates': setSheetCandidates(res.candidates); break;
        case 'piece':
          setPiece({ piece: res.piece, jobId: res.jobId, jobName: res.jobName });
          setSheet(null); setJobData(null); setView('piece'); setInput('');
          break;
        case 'pieceCandidates': setPieceCandidates(res.candidates); break;
        case 'artifact': setError(t('terminal.error.artifact')); break;
        case 'unknown': setError(t('terminal.error.unknown')); break;
        case 'notfound': setError(t('terminal.error.notfound')); break;
        case 'empty': break;
        default: setError(t('terminal.error.unknown'));
      }
    } catch (err) {
      setError(err.message || t('terminal.error.generic'));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e) => { e.preventDefault(); handleResolve(input); };

  const onCameraScan = (decoded) => { setCameraOpen(false); handleResolve(decoded); };

  const selectSheetCandidate = async (cand) => {
    setBusy(true); setError('');
    try {
      const loaded = await loadSheet(db, cand.jobId, cand.id);
      if (!loaded) { setError(t('terminal.error.sheetGone')); return; }
      showSheet(loaded);
    } catch (err) {
      setError(err.message || t('terminal.error.generic'));
    } finally { setBusy(false); }
  };

  const selectPieceCandidate = (r) => {
    setPiece({ piece: r.piece, jobId: r.jobId, jobName: r.jobId });
    setSheet(null); setJobData(null); setPieceCandidates([]); setView('piece');
  };

  // B1a: el marcado se hace SERVER-SIDE (valida contadores/permiso + emite al bus ITC).
  const handleMark = async (type) => {
    if (!sheet) return;
    setBusy(true); setError(''); setSuccess('');
    try {
      const recordProductionScan = httpsCallable(functions, 'recordProductionScan');
      await recordProductionScan({ jobId: sheet.jobId, sheetId: sheet.id, type });
      const loaded = await loadSheet(db, sheet.jobId, sheet.id);
      if (loaded) { setSheet(loaded.sheet); setJobData(loaded.jobData); }
      setSuccess(t(type === 'print' ? 'terminal.success.print' : type === 'printBack' ? 'terminal.success.printBack' : 'terminal.success.cut'));
    } catch (err) {
      setError(err.message || t('terminal.error.generic'));
    } finally { setBusy(false); }
  };

  // Falla: mismo writeBatch que /scan y SheetDetailModal (tarea asignada + correo + history).
  const handleReportFailure = async ({ failureType, failedCount, reason, assignedUser, aiCleanedDescription, aiSuggestedAction }) => {
    if (!sheet || !jobData) return;
    const sheetName = sheet.hri;
    const sheetRef = doc(db, 'jobs', jobData.id, 'sheets', sheet.id);
    const batch = writeBatch(db);
    const finalDescription = aiCleanedDescription || reason;
    const isDesign = failureType === 'design';

    const baseTask = {
      jobId: jobData.id,
      sheetId: sheet.id,
      relatedPlateId: sheet.id,
      status: 'pending',
      createdBy: user.email,
      createdByName: user.displayName || user.email,
      createdAt: serverTimestamp(),
      originalDescription: reason,
      aiSuggestedAction: aiSuggestedAction || null,
      ...buildTaskAssignment(assignedUser),
      ...buildAssignedByStamp(user),
    };

    let taskData;
    if (isDesign) {
      batch.update(sheetRef, {
        status: 'Devuelto a Diseño',
        hasActiveAlert: true,
        history: arrayUnion({ action: 'Devuelto a Diseño desde Terminal', details: `Motivo: ${finalDescription}`, user: user.email, timestamp: new Date() }),
      });
      taskData = { ...baseTask, type: 'system', title: `Corrección: Plancha ${sheetName}`, description: finalDescription };
    } else {
      // Contadores a cero AQUÍ: el ciclo de reproducción arranca al reportar la falla.
      batch.update(sheetRef, {
        ...productionResetFields(),
        hasActiveAlert: true,
        history: arrayUnion({ action: 'Falla de Producción Reportada', details: `${failedCount} plancha(s) dañada(s). Razón: ${finalDescription}`, user: user.email, timestamp: new Date() }),
      });
      taskData = { ...baseTask, type: 'system_reproduction', title: `Reproducción: ${failedCount}x Plancha ${sheetName}`, description: `Se solicitaron ${failedCount} planchas de reposición. Causa: ${finalDescription}` };
    }

    batch.set(doc(collection(db, 'jobs', jobData.id, 'tasks')), taskData);
    const mail = buildAssignmentEmail({ assignedUser, assignedBy: user, taskTitle: taskData.title, taskDescription: taskData.description, jobId: jobData.id, suggestedAction: aiSuggestedAction });
    if (mail) batch.set(doc(collection(db, 'mail')), mail);

    try {
      await batch.commit();
      setReportOpen(false);
      setSuccess(t('terminal.success.report'));
      resetToScan();
    } catch (err) {
      setError(`${t('terminal.error.generic')} ${err.message}`);
    }
  };

  const avail = sheet ? markAvailability(sheet) : null;
  const btn = 'w-full min-h-[72px] text-xl font-bold rounded-xl flex items-center justify-center gap-3 disabled:bg-gray-600 disabled:opacity-60';

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      <CameraScanner isOpen={cameraOpen} onScan={onCameraScan} onClose={() => setCameraOpen(false)} />
      {sheet && jobData && (
        <ReportFailureModal
          isOpen={reportOpen}
          onClose={() => setReportOpen(false)}
          onSubmit={handleReportFailure}
          sheetName={sheet.hri}
          users={users}
          initialFailureType="production"
          sheetContext={`${t('terminal.job')}: ${jobData.jobName} | reps: ${sheet.repetitions || 1}`}
        />
      )}

      {/* Barra superior (sin sidebar) */}
      <header className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-3 min-w-0">
          {view !== 'scan' ? (
            <button onClick={resetToScan} className="min-h-[56px] px-4 bg-gray-700 rounded-xl flex items-center gap-2 font-bold">
              <ArrowLeft size={22} /> {t('terminal.next')}
            </button>
          ) : (
            <h1 className="text-2xl font-bold truncate">{t('terminal.title')}</h1>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden sm:block text-sm text-gray-300 truncate max-w-[180px]" title={user?.email}>{user?.displayName || user?.email}</span>
          <button onClick={() => navigate('/scan')} className="min-h-[56px] px-4 bg-gray-700 rounded-xl flex items-center gap-2 font-semibold">
            <LogOut size={20} /> {t('terminal.exit')}
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 flex flex-col items-center">
        <div className="w-full max-w-3xl">
          {/* Mensajes */}
          {error && <div className="mb-4 p-4 bg-red-500/20 text-red-200 rounded-xl flex items-center gap-2 text-lg"><AlertCircle size={22} /> {error}</div>}
          {success && <div className="mb-4 p-4 bg-green-500/20 text-green-200 rounded-xl flex items-center gap-2 text-lg"><CheckCircle size={22} /> {success}</div>}

          {/* ── Modo ESCANEO ── */}
          {view === 'scan' && (
            <>
              <p className="text-center text-gray-300 text-lg mb-4">{t('terminal.subtitle')}</p>
              <form onSubmit={onSubmit} className="flex gap-2 mb-4">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={t('terminal.input.placeholder')}
                  autoFocus
                  className="w-full p-4 text-lg bg-gray-800 border-2 border-gray-700 rounded-xl focus:border-indigo-500 focus:outline-none"
                />
                <button type="submit" disabled={busy} className="min-h-[64px] px-6 bg-indigo-600 rounded-xl font-bold disabled:bg-gray-600">
                  <Search size={26} />
                </button>
              </form>
              <button onClick={() => setCameraOpen(true)} className={`${btn} bg-indigo-700 hover:bg-indigo-600`}>
                <Camera size={26} /> {t('terminal.scanCamera')}
              </button>
              {busy && <p className="text-center mt-4 text-gray-300">{t('terminal.searching')}</p>}

              {sheetCandidates.length > 0 && (
                <div className="mt-6 space-y-2">
                  <p className="text-gray-300">{t('terminal.candidates.sheets')}</p>
                  {sheetCandidates.map((c) => (
                    <button key={c.id} onClick={() => selectSheetCandidate(c)} className="w-full text-left p-3 bg-gray-800 rounded-xl flex items-center gap-4 hover:bg-gray-700">
                      <img src={previewThumb(c) || PLACEHOLDER_PIECE} alt="" className="w-20 h-14 rounded object-cover" />
                      <div><p className="font-bold text-lg">{c.hri}</p><p className="text-sm text-gray-400">{c.productionStatus || c.status}</p></div>
                    </button>
                  ))}
                </div>
              )}

              {pieceCandidates.length > 0 && (
                <div className="mt-6 space-y-2">
                  <p className="text-gray-300">{t('terminal.candidates.pieces')}</p>
                  {pieceCandidates.map((r) => (
                    <button key={r.jobId} onClick={() => selectPieceCandidate(r)} className="w-full text-left p-3 bg-gray-800 rounded-xl flex items-center gap-4 hover:bg-gray-700">
                      <img src={r.piece.previewUrl || PLACEHOLDER_PIECE} alt="" className="w-16 h-16 rounded object-contain" />
                      <div><p className="font-bold text-lg font-mono">{r.piece.code}</p><p className="text-sm text-gray-400">{r.piece.name} · {t('terminal.job')} {r.jobId}</p></div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── Modo PLANCHA ── */}
          {view === 'sheet' && sheet && jobData && avail && (
            <div className="bg-gray-800 rounded-2xl p-4 sm:p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="min-w-0">
                  <h2 className="text-3xl font-bold truncate">{sheet.hri}</h2>
                  <p className="text-gray-400">{t('terminal.job')}: {jobData.jobName} ({jobData.id})</p>
                </div>
              </div>

              <img src={previewCard(sheet) || PLACEHOLDER_SHEET} alt={t('terminal.preview')} className="w-full max-h-72 object-contain rounded-xl border border-gray-700 bg-gray-900 mb-4" />

              <div className={`grid gap-3 mb-5 ${avail.twoSided ? 'grid-cols-3' : 'grid-cols-2'}`}>
                <Stat label={avail.twoSided ? t('terminal.printedFront') : t('terminal.printed')} value={avail.printedFront} total={avail.reps} />
                {avail.twoSided && <Stat label={t('terminal.printedBack')} value={avail.printedBack} total={avail.reps} />}
                <Stat label={t('terminal.cutLabel')} value={avail.cut} total={avail.reps} />
              </div>

              <p className="text-center text-lg font-semibold text-gray-200 mb-3">{t('terminal.action.prompt')}</p>
              <div className="space-y-3">
                {avail.isPrinting && (
                  <button onClick={() => handleMark('print')} disabled={busy || !avail.canPrint} className={`${btn} bg-blue-600 hover:bg-blue-500`}>
                    <Printer size={26} /> {avail.twoSided ? t('terminal.markPrintFront') : t('terminal.markPrint')}
                  </button>
                )}
                {avail.twoSided && (
                  <button onClick={() => handleMark('printBack')} disabled={busy || !avail.canPrintBack} className={`${btn} bg-indigo-600 hover:bg-indigo-500`}>
                    <Printer size={26} /> {t('terminal.markPrintBack')}
                  </button>
                )}
                <button onClick={() => handleMark('cut')} disabled={busy || !avail.canCut} className={`${btn} bg-green-600 hover:bg-green-500`}>
                  <Scissors size={26} /> {t('terminal.markCut')}
                </button>
                <button onClick={() => setReportOpen(true)} disabled={busy} className={`${btn} bg-red-600 hover:bg-red-500`}>
                  <AlertTriangle size={26} /> {t('terminal.reportFailure')}
                </button>
                {!avail.canPrint && !avail.canPrintBack && !avail.canCut && (
                  <p className="text-center text-amber-300 pt-1">{t('terminal.allMarked')}</p>
                )}
              </div>

              {sheet.pieces?.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold mb-2">{t('terminal.piecesInSheet')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {sheet.pieces.map((p) => (
                      <div key={p.code} className="p-2 bg-gray-700 rounded-lg text-center">
                        <img src={p.previewUrl || PLACEHOLDER_PIECE} alt={p.code} className="w-16 h-16 object-contain rounded mx-auto" />
                        <p className="text-xs mt-1 font-mono">{p.code}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={resetToScan} className={`${btn} bg-gray-700 hover:bg-gray-600 mt-6`}>
                <RotateCcw size={24} /> {t('terminal.next')}
              </button>
            </div>
          )}

          {/* ── Modo PIEZA (read-only) ── */}
          {view === 'piece' && piece && (
            <div className="bg-gray-800 rounded-2xl p-4 sm:p-6">
              <div className="flex justify-between items-start border-b border-gray-700 pb-4 mb-4">
                <div className="min-w-0">
                  <h2 className="text-2xl font-bold font-mono truncate">{piece.piece.code}</h2>
                  <p className="text-sm text-gray-400">{piece.piece.name} · {t('terminal.job')}: {piece.jobName} ({piece.jobId})</p>
                </div>
                <button onClick={resetToScan} className="p-2 hover:bg-gray-700 rounded-full"><X size={22} /></button>
              </div>
              <div className="flex justify-center mb-4">
                <img src={piece.piece.previewUrl || PLACEHOLDER_SHEET} alt="" className="max-h-56 object-contain rounded-xl border border-gray-700" />
              </div>
              <div className="grid grid-cols-3 gap-3 text-center mb-4">
                <Stat label={t('terminal.piece.required')} value={piece.piece.totalQuantity} total={piece.piece.totalQuantity} />
                <Stat label={t('terminal.piece.printed')} value={piece.piece.printedQuantity} total={piece.piece.totalQuantity} />
                <Stat label={t('terminal.piece.cut')} value={piece.piece.cutQuantity} total={piece.piece.totalQuantity} />
              </div>
              <div className="mb-3">
                <p className="text-sm text-gray-400 mb-1">{t('terminal.piece.status')}</p>
                <span className="px-3 py-1 inline-flex text-sm font-semibold rounded-full bg-gray-200 text-gray-900">{pieceProductionStatus(piece.piece).text}</span>
              </div>
              <div className="mb-3">
                <p className="text-sm text-gray-400 mb-1">{t('terminal.piece.inSheets')}</p>
                <div className="flex flex-wrap gap-1">
                  {Array.from(piece.piece.sheets || []).map((hri) => (
                    <span key={hri} className="bg-gray-700 px-2 py-0.5 rounded-full text-xs font-mono">{hri}</span>
                  ))}
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-4">{t('terminal.piece.readonly')}</p>
              <button onClick={resetToScan} className={`${btn} bg-gray-700 hover:bg-gray-600`}>
                <RotateCcw size={24} /> {t('terminal.next')}
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Aviso de inactividad (respeta la estación; en tablet-personal casi nunca aparece) */}
      {warning && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[130]">
          <div className="bg-white text-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 text-center">
            <h2 className="text-xl font-bold mb-2">¿Sigues ahí?</h2>
            <p className="text-4xl font-extrabold text-red-600 mb-4 tabular-nums">{secondsLeft}s</p>
            <div className="flex justify-center gap-3">
              <button onClick={onLogout} className="py-3 px-5 font-bold bg-gray-200 rounded-xl">{t('terminal.logout')}</button>
              <button onClick={stayActive} className="py-3 px-5 font-bold text-white bg-blue-600 rounded-xl">Seguir conectado</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TerminalView;
